<template>
  <div
    id="select-date-time"
    class="row"
  >
    <div class="col-md-6">
      <div class="col-heading">
        <h3 class="mb-4">
          Dates and times
        </h3>
      </div>
      <div class="card">
        <div class="row">
          <div class="form-group col-6">
            <label for="entryDate">{{ firstOptionLabel }} date</label>
            <div class="input-wrapper">
              <mag-datepicker-single
                :block="true"
                :placeholder="firstOptionLabel + ' date'"
                :initial-start-date="entryDatePickerOptions.selectedDate"
                :selectable-ranges="entryDatePickerOptions.enabledDates"
                :show-clear-date="true"
                :disabled="disabled"
                @date-changed="(val) => entryDate = val"
              />
            </div>
          </div>
          <div class="form-group col">
            <label for="entryTime">{{ firstOptionLabel }} time</label>
            <div class="input-wrapper">
              <select
                id="entryTime"
                v-model="entryTime"
                :disabled="disabled"
                class="form-control"
              >
                <option
                  value="invalid"
                  disabled
                  selected
                >
                  Select a time
                </option>
                <option
                  v-for="(option, index) in timePickerOptions()"
                  :key="index"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <i class="icon--arrow-dropdown float-right input-icon" />
            </div>
          </div>
          <small
            v-if="!isEntryAtLeastOneHourInFuture"
            class="col-12 text--error"
          >Bookings cannot be pre created less than 1 hours in advance. Parking is available at the airport.</small>
        </div>
        <div
          v-show="showExitOptions"
          class="row"
        >
          <div class="form-group col">
            <label for="exitDate">{{ secondOptionLabel }} date</label>
            <div class="input-wrapper">
              <mag-datepicker-single
                :block="true"
                :placeholder="secondOptionLabel + ' date'"
                :initial-start-date="exitDatePickerOptions.selectedDate"
                :selectable-ranges="exitDatePickerOptions.enabledDates"
                :show-clear-date="true"
                :disabled="(!entryDateAndTimeSelected && !allowExitOnlySelect) || disabled"
                @date-changed="(val) => exitDate = val"
              />
            </div>
          </div>
          <div class="form-group col">
            <label for="exitTime">{{ secondOptionLabel }} time</label>
            <div class="input-wrapper">
              <select
                id="exitTime"
                v-model="exitTime"
                :disabled="(!entryDateAndTimeSelected && !allowExitOnlySelect) || disabled"
                class="form-control"
              >
                <option
                  value="invalid"
                  disabled
                  selected
                >
                  Select a time
                </option>
                <option
                  v-for="(option, index) in timePickerOptions()"
                  :key="index"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <i class="icon--arrow-dropdown float-right input-icon" />
            </div>
          </div>
          <small
            v-if="isExitBeforeEntry"
            class="col-12 text--error"
          >Please ensure the entry date &amp; time is before the exit date &amp; time</small>
          <small
            v-if="!isEntryAtLeastOneHourInFuture && !entryDateAndTimeSelected"
            class="col-12 text--error"
          >Bookings cannot be pre created less than 1 hours in advance. Parking is available at the airport.</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'

const moment = require('moment-timezone')

export default {
    name: 'SelectDateTime',
    components: {
        Datepicker
    },
    props: {
        timePickerConfig: {
            type: Object,
            default: null
        },
        showExitOptions: {
            type: Boolean,
            default: true
        },
        allowExitOnlySelect: {
            type: Boolean,
            default: false
        },
        autoExitSelect: {
            type: Boolean,
            default: true
        },
        firstOptionLabel: {
            type: String,
            default: 'Entry'
        },
        secondOptionLabel: {
            type: String,
            default: 'Exit'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        timeZone: {
            type: String,
            default: 'Europe/London'
        }
    },
    data () {
        return {
            commonDatePickerOptions: {
                typeable: false,
                format: 'dd/MM/yyyy'
            },
            entryDatePickerOptions: {
                selectedDate: null, // dates: [new Date(moment().tz(this.timeZone)).format('YYYY-MM-DDTHH:mm:ss'))]
                enabledDates: [[moment().tz(this.timeZone), moment().tz(this.timeZone).add(5, 'years')]]
            },
            exitDatePickerOptions: {
                selectedDate: null, // dates: [new Date(moment().tz(this.timeZone)).format('YYYY-MM-DDTHH:mm:ss'))]
                enabledDates: [[moment().tz(this.timeZone), moment().tz(this.timeZone).add(5, 'years')]]
            },
            defaultConfig: {
                start: '00:00',
                interval: 60,
                period: 'minutes'
            }
        }
    },
    computed: {
        isExitBeforeEntry: function () {
            if (moment(this.entryDateTime).isAfter(moment(this.exitDateTime))) {
                return true
            } else {
                return false
            }
        },
        entryDate: {
            get () {
                return this.$store.state.booking.entryAndExit.entryDate
            },
            async set (value) {
                await this.$store.dispatch('updateEntryDate', moment(value).toDate())

                if (this.entryDate !== null && this.entryTime !== 'invalid') {
                    this.entryDateTime = this.asCombinedMoment(
                        this.entryDate,
                        this.entryTime
                    )
                }
            }
        },
        isEntryAtLeastOneHourInFuture: function () {
            if (!this.entryDateTime && !this.exitDateTime) return false
            if (this.allowExitOnlySelect && this.exitDateTime && !this.entryDateTime) {
                return moment.utc(moment().tz(this.timeZone).format('YYYY-MM-DDTHH:mm:ss')).add(1, 'hour').isSameOrBefore(moment(this.exitDateTime))
            }
            return moment.utc(moment().tz(this.timeZone).format('YYYY-MM-DDTHH:mm:ss')).add(1, 'hour').isSameOrBefore(moment(this.entryDateTime))
        },
        entryTime: {
            get () {
                const current = this.$store.state.booking.entryAndExit.entryTime
                return current !== null ? current : 'invalid'
            },
            async set (value) {
                await this.$store.dispatch('updateEntryTime', value)

                if (this.entryDate !== null && this.entryTime !== 'invalid') {
                    this.entryDateTime = this.asCombinedMoment(
                        this.entryDate,
                        this.entryTime
                    )
                }
            }
        },
        exitDate: {
            get () {
                return this.$store.state.booking.entryAndExit.exitDate
            },
            async set (value) {
                await this.$store.dispatch('updateExitDate', value)
                await this.$store.dispatch('updateDateTimeValid', this.isEntryAtLeastOneHourInFuture)
                if (this.exitDate !== null && this.exitTime !== 'invalid') {
                    this.exitDateTime = this.asCombinedMoment(
                        this.exitDate,
                        this.exitTime
                    )
                }
            }
        },
        exitTime: {
            get () {
                const current = this.$store.state.booking.entryAndExit.exitTime
                return current !== null ? current : 'invalid'
            },
            async set (value) {
                await this.$store.dispatch('updateExitTime', value)
                await this.$store.dispatch('updateDateTimeValid', this.isEntryAtLeastOneHourInFuture)
                if (this.exitDate !== null && this.exitTime !== 'invalid') {
                    this.exitDateTime = this.asCombinedMoment(
                        this.exitDate,
                        this.exitTime
                    )
                }
            }
        },
        entryDateTime: {
            get () {
                return this.$store.state.booking.entryAndExit.entryDateTime
            },
            async set (value) {
                await this.$store.dispatch('updateEntryDateTime', value)
                await this.$store.dispatch('updateDateTimeValid', this.isEntryAtLeastOneHourInFuture)
            }
        },
        exitDateTime: {
            get () {
                return this.$store.state.booking.entryAndExit.exitDateTime
            },
            async set (value) {
                await this.$store.dispatch('updateExitDateTime', value)
                await this.$store.dispatch('updateDateTimeValid', this.isEntryAtLeastOneHourInFuture)
            }
        },
        entryDateAndTimeSelected () {
            return this.entryDateTime !== null
        },
        exitDateAndTimeSelected () {
            return this.exitDateTime !== null
        },
        entryAndExitSelected () {
            return (
                this.entryDateAndTimeSelected &&
                this.exitDateAndTimeSelected &&
                moment(this.exitDateTime).isAfter(moment(this.entryDateTime))
            )
        }
    },
    watch: {
        entryDate () {
            if (
                this.autoExitSelect &&
                (this.exitDate < this.entryDate ||
                (this.exitDate === null && this.entryDate !== null))
            ) {
                this.exitDate = new Date(moment(this.entryDate).add(1, 'day'))
            }
        }
    },
    methods: {
        timePickerOptions () {
            let start = this.defaultConfig.start
            let interval = this.defaultConfig.interval
            let period = this.defaultConfig.period
            if (this.timePickerConfig !== null) {
                start = this.timePickerConfig.start || this.defaultConfig.start
                interval =
                    this.timePickerConfig.interval || this.defaultConfig.interval
                period = this.timePickerConfig.period || this.defaultConfig.period
            }

            const periodsInADay = moment.duration(1, 'day').as(period)
            const options = []
            const startTimeMoment = moment(start, 'HH:mm')

            for (let i = 0; i < periodsInADay; i += interval) {
                startTimeMoment.add(i === 0 ? 0 : interval, period)
                options.push(startTimeMoment.format('HH:mm'))
            }

            return options
        },
        asCombinedMoment (date, timeAsString) {
            const hoursAndMinutes = timeAsString.split(':')
            return moment(date)
                .utc()
                .startOf('day')
                .add(hoursAndMinutes[0], 'hours')
                .add(hoursAndMinutes[1], 'minutes')
                .toDate()
        },
        async resetBookingEntryAndExit () {
            await this.$store.dispatch('resetEntryAndExit')
        }
    }
}
</script>
